import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetIdentity, useUrlParamValue } from 'src/hooks';
import { TProgramListRequest } from 'src/services/apiEndpoint.types.ts';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { programList, resetPrograms } from 'src/store/program/program.slice';
import { Separator, Typography } from 'src/components/common';
import Button from 'src/components/button/Button';
import { SelfCompanyContext } from 'src/pages/myCompany/MyCompany';

import { TFilterProgram, TProgramFilterType, TProgramType, TProgramsProps } from './programs.types';
import './Event.scss';
import { httpRequest } from 'src/services/commonApis';
import CreateProgramCard from '../../programs/createProgramCard/CreateProgramCard';
import ProgramCard from '../../programs/programCard/ProgramCard';
import ProgramFilters from '../../programs/programFilters/ProgramFilters';

const Events = ({ companyId, isViewerProgramCreator = true }: TProgramsProps) => {
  // Hooks
  const { getIdentities } = useGetIdentity();
  const { paramValue } = useUrlParamValue();
  const navigate = useNavigate();
  const { programsPageNumber, programsPerPage, programs } = useAppSelector(
    (store) => store.program
  );
  const { isUserKeyMember } = useAppSelector((store) => store.company.viewCompany);
  const dispatch = useAppDispatch();
  const selfCompany = useContext(SelfCompanyContext);

  // React state
  const [userProgramFilter, setUserProgramFilter] = useState<'All' | 'Access'>('All');
  const [draftEvents, setDraftEvents] = useState<any>([])

  const { loggedInUserType, loggedInUserId } = getIdentities();

  const programListParams = useMemo(() => {
    const programType: TProgramType = (paramValue({ paramName: 'programType' }) ||
      'Active') as TProgramType;
    const filterProgram: TFilterProgram = (paramValue({ paramName: 'filterProgram' }) ||
      'All') as TFilterProgram;

    return { programType, filterProgram };
  }, [window.location.search]);

  const updateParams = ({ param, value }: { param: TProgramFilterType; value: string }) => {
    let path = '';

    if (selfCompany) {
      path = `${window.location.pathname}?tab=events&${param}=${value}`;
    } else {
      path = `${window.location.pathname}?id=${companyId}&tab=events&${param}=${value}`;
    }

    navigate(path);
  };

  const getProgramList = ({ refreshPrograms }: { refreshPrograms: boolean }) => {
    const { programType } = programListParams;
    // const { filterProgram } = programListParams;
    const payload: TProgramListRequest = {
      companyId,
      limit: programsPerPage,
      type: 'event',

      offset: refreshPrograms ? 1 : programsPageNumber,
      ...(loggedInUserType === 'User' && loggedInUserId && { userId: loggedInUserId }),
      ...(programType && { eventStatus: programType }),
      ...(userProgramFilter && loggedInUserType === 'User' && { filterProgram: userProgramFilter })
    };

    dispatch(programList(payload));
  };
  const { programType } = programListParams;

  const programPayload: TProgramListRequest = {
    companyId,
    limit: programsPerPage,
    type: 'event',
    offset: programsPageNumber,
    ...(loggedInUserType === 'User' && loggedInUserId && { userId: loggedInUserId }),
    ...(programType && { eventStatus: programType }),
    ...(userProgramFilter && loggedInUserType === 'User' && { filterProgram: userProgramFilter })
  };

  useEffect(() => {
    getProgramList({ refreshPrograms: false });
  }, [programsPageNumber, programsPerPage, window.location.search, userProgramFilter]);

  useEffect(() => {
    return () => {
      dispatch(resetPrograms());
    };
  }, []);

  const getDraftEvents = async (route: string) => {
    const response = await httpRequest(route, 'GET', null, 'json', 'program');
    if (response?.message === 'Drafts') {
      setDraftEvents([...draftEvents, ...response?.data?.events])
    }
  }
  console.log(draftEvents, 'draftEvents');
  
  useEffect(() => {
    if (programType === 'All') {
      const { programType } = programListParams;
      // const { filterProgram } = programListParams;
      const payload: any = {
        type: 'event',
        offset: programsPageNumber,
        ...(loggedInUserType === 'User' && loggedInUserId && { userId: loggedInUserId }),
        ...(programType && { eventStatus: 'Draft' }),
        ...(userProgramFilter && loggedInUserType === 'User' && { filterProgram: userProgramFilter })
      };
      const route = `global-events/list?companyId=${companyId}&limit=${programsPerPage}&offset=${payload?.offset}&eventStatus=Draft`;
      getDraftEvents(route)
    }
  }, [programType, programsPageNumber, programsPerPage, window.location.search, userProgramFilter])

  const modifiedProgramList = programType === 'All' ? [...draftEvents, ...programs] : programs;
  return (
    <div className="company-programs">
      <div className="company-programs__header">
        <Typography variant="subHeading-2" fontWeight="semiBold">
          Events
        </Typography>
        {loggedInUserType === 'User' && isUserKeyMember && isViewerProgramCreator && <Separator />}
        {loggedInUserType === 'Company' && isViewerProgramCreator && <ProgramFilters updateParams={updateParams} />}
        {loggedInUserType === 'User' && (isUserKeyMember) && isViewerProgramCreator && (
          <div className="company-programs__header__buttons">
            <Button
              size="small"
              className="company-programs__header__buttons__all"
              state={userProgramFilter === 'All' ? 'active' : 'default'}
              variant="tertiary"
              onClick={() => {
                setUserProgramFilter('All');
              }}
            >
              All Events
            </Button>
            <Button
              size="small"
              className="company-programs__header__buttons__access"
              state={userProgramFilter === 'Access' ? 'active' : 'default'}
              variant="tertiary"
              onClick={() => {
                setUserProgramFilter('Access');
              }}
            >
              Access Events
            </Button>
          </div>
        )}
      </div>
      <div className="company-programs__programs">
        {loggedInUserType === 'Company' && selfCompany && <CreateProgramCard title="Event" navigateTo='/create-event' />}
        {modifiedProgramList?.map((program, index) => {
          console.log(program, 'programDAta');

          const isDraft = program?.EventDraft !== undefined
          return (
            <ProgramCard
              key={index}
              {...program}
              type='event'
              isViewerProgramCreator={isViewerProgramCreator}
              getProgramList={getProgramList}
              programType={isDraft ? 'Draft' : programType}
              programMemberView={userProgramFilter === 'Access'}
              programPayload={programPayload}
              getDraftProgram={getDraftEvents}
            />
          )
        })}
      </div>
    </div>
  );
};

export default Events;
